import { HttpClient } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { Observable } from 'rxjs'
import { map } from 'rxjs/operators'
import { environment } from '../../../../environments/environment'
import { AuthService } from '../../auth/services/auth.service'
import { InvitationJobsResponse } from '../models/invitation-job.interface'
import { Invitation, InvitationCounts, InvitationResponse, InvitationStatus } from '../models/invitation-tracking.interface'

/**
 * Service for managing invitations data
 * Handles API communication for invitation-related operations
 */
@Injectable({
  providedIn: 'root'
})
export class InvitationsService {
  private readonly _apiUrl = `${environment.baseContactlessApi}v3/dashboards/invitation`

  constructor(private readonly _http: HttpClient, private readonly _auth: AuthService) {}

  /**
   * Gets a list of invitations for an event with optional filtering
   * @param eventId - The event ID to get invitations for
   * @param keyword - Optional search keyword
   * @param page - Page number (1-based)
   * @param perPage - Number of items per page
   * @param status - Optional status filter
   * @returns Observable of invitation response data
   */
  getInvitations(eventId: string, keyword = '', page = 1, perPage = 50, status?: InvitationStatus): Observable<InvitationResponse> {
    const params: any = {
      to_event: eventId,
      keyword,
      page: page.toString(),
      perpage: perPage.toString()
    }

    if (status && status !== 'all') params.status = status

    return this._http.get<InvitationResponse>(`${this._apiUrl}/list`, { params })
  }

  /**
   * Gets invitation counts for an event
   * @param eventId - The event ID to get counts for
   * @returns Observable of invitation counts
   */
  getInvitationCounts(eventId: string): Observable<InvitationCounts> {
    return this._http.get<InvitationCounts>(`${this._apiUrl}/counts`, {
      params: { to_event: eventId }
    })
  }

  /**
   * Recent bulk invite jobs for workspace (optionally filtered by source/target event).
   */
  getInvitationJobs(workspaceSlug: string, filters?: { from_event_id?: number; to_event_id?: number; limit?: number; days?: number }): Observable<InvitationJobsResponse> {
    const params: Record<string, string> = {}
    if (filters?.from_event_id != null) {
      params.from_event_id = String(filters.from_event_id)
    }
    if (filters?.to_event_id != null) {
      params.to_event_id = String(filters.to_event_id)
    }
    if (filters?.limit != null) {
      params.limit = String(filters.limit)
    }
    if (filters?.days != null) {
      params.days = String(filters.days)
    }
    return this._http.get<InvitationJobsResponse>(`${this._apiUrl}/jobs/${workspaceSlug}`, { params })
  }
}
